import React, { useState, useEffect, useContext, useCallback, useMemo, useRef } from "react";
import UniversalProvider, { Namespace } from "@walletconnect/universal-provider";
import { TMessageCreateData } from "../types/clientTypes";
import { initializeModal, initializeProvider } from "./configWC";
import { SessionTypes } from "@walletconnect/types";
import { useDisconnect } from "@reown/appkit/react";
import { COSMOS_CHAIN_ID, WIKEY_CHAIN_ID } from "../utils/constants";
import { SendRequestResult, SendRequestTimeoutError } from "../utils/errors";
import { logger } from "../lib/logger";
import { ErrorCodes } from "../lib/errorCodes";

let CHAIN_ID = WIKEY_CHAIN_ID;

const omnistarNamespace = {
  methods: ["self_sendDirect"],
  chains: ["omnistar:self"],
  events: ["connect", "disconnect"],
} as Namespace;

const cosmosNamespace = {
  methods: ["self_sendDirect"],
  chains: ["omnistar:self"],
  events: ["connect", "disconnect"],
} as Namespace;

const namespaces = {
  omnistar: omnistarNamespace,
  cosmos: cosmosNamespace,
};

const projectId = import.meta.env.VITE_PROJECT_ID;
if (!projectId) {
  throw new Error("You need to provide VITE_PROJECT_ID env variable");
}

const extractAccountAddressFromSession = (session: SessionTypes.Struct) => {
  let account = session?.namespaces["omnistar"]?.accounts.at(-1)?.split(":").slice(-1)?.[0] || null;
  if (!account) {
    account = session?.namespaces["cosmos"].accounts.at(-1).split(":").slice(-1)[0] || null;
    CHAIN_ID = COSMOS_CHAIN_ID;
  } else {
    CHAIN_ID = WIKEY_CHAIN_ID;
  }
  return account;
};

export type connectionContextType = {
  isConnected: boolean;
  address: string | null;
  session: any | null;
  isLoading: boolean;
  account: string | null;
  providerClient: UniversalProvider | undefined;
  connect: () => Promise<void>;
  disconnect: () => Promise<void>;
  sendRequest: (
    messages: TMessageCreateData[]
  ) => Promise<{ ok: true; data: string } | { ok: false; error: Error }>;
};

export const ConnectionContext = React.createContext<connectionContextType | null>(null);

export const ConnectionProvider = ({ children }: { children?: React.ReactNode }) => {
  const { disconnect } = useDisconnect();
  const [providerClient, setProviderClient] = useState<UniversalProvider | null>(null);
  const [account, setAccount] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [session, setSession] = useState<any | null>(null);
  const [isConnected, setIsConnected] = useState<boolean>(false);

  // Ref to track connection timeout for cleanup
  const connectionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Ref to prevent concurrent/duplicate disconnect attempts
  const isDisconnectingRef = useRef(false);

  // connect attempt
  const connect = useCallback(async () => {
    try {
      if (!providerClient) {
        throw new Error("Provider not initialized");
      }

      // Clear any existing timeout before starting a new connection attempt
      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
        connectionTimeoutRef.current = null;
      }

      // Set timeout to cleanup if proposal expires (3 minutes)
      connectionTimeoutRef.current = setTimeout(
        async () => {
          if (providerClient && !isConnected && !isLoading && !providerClient.isWalletConnect) {
            await providerClient?.cleanupPendingPairings();
            console.log("Proposal expired, cleaning up...");
            // Don't auto-retry - let user manually retry if needed
          }
          connectionTimeoutRef.current = null;
        },
        3 * 60 * 1000
      );

      // first clean up the recent wallets sessions of reown. so the button "recent" will not show the old sessions.
      localStorage.removeItem("@appkit/recent_wallet");
      localStorage.removeItem("@appkit/recent_wallets");

      const session = await providerClient
        .connect({
          namespaces,
        })
        .catch((error) => {
          providerClient?.cleanupPendingPairings();
          // user rejected: {code: 5000, message: 'User rejected.'}
          // proposal expired: {message: 'Proposal expired', code: 0}
          if (error.message === "Proposal expired") {
            logger.error(
              "walletconnect",
              ErrorCodes.WC_001,
              "WalletConnect proposal expired — user did not approve in time",
              error,
              {
                wc_code: error.code,
              }
            );
            connect();
          } else if (error.message === "User rejected.") {
            logger.warn(
              "walletconnect",
              ErrorCodes.WC_001,
              "User rejected WalletConnect connection",
              { wc_code: error.code }
            );
            connect();
          } else {
            logger.error(
              "walletconnect",
              ErrorCodes.WC_001,
              "WalletConnect connect() failed",
              error,
              { wc_code: error.code }
            );
            throw error;
          }
        });

      // Clear timeout on successful connection
      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
        connectionTimeoutRef.current = null;
      }

      setSession(session);
      setIsConnected(!!session);
      setIsLoading(false);
    } catch (error) {
      // Clear timeout on error
      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
        connectionTimeoutRef.current = null;
      }

      if (error instanceof Error && error.message === "Proposal expired") {
        logger.error(
          "walletconnect",
          ErrorCodes.WC_001,
          "WalletConnect proposal expired (outer catch)",
          error
        );
        try {
          await providerClient?.cleanupPendingPairings();
        } catch (cleanupError) {
          logger.error(
            "walletconnect",
            ErrorCodes.WC_001,
            "Cleanup after proposal expiry failed",
            cleanupError as Error
          );
        }
      } else {
        logger.error(
          "walletconnect",
          ErrorCodes.WC_001,
          "WalletConnect connection error (outer catch)",
          error as Error
        );
      }
      setIsLoading(false);
    }
  }, [providerClient, isConnected, isLoading]);

  const handleDisconnect = useCallback(async () => {
    // Prevent concurrent/duplicate disconnect attempts
    if (isDisconnectingRef.current) return;
    isDisconnectingRef.current = true;

    try {
      // Only call disconnect if session still exists (SDK hasn't cleaned up yet)
      if (providerClient?.session) {
        await providerClient.disconnect();
      }
    } catch (error) {
      // Ignore "already disconnected" errors - this is expected for wallet-initiated disconnects
      if (error instanceof Error && !error.message.includes("Please call connect()")) {
        logger.error("walletconnect", ErrorCodes.WC_002, "WalletConnect disconnect error", error);
      }
    }

    // Always clean up local state
    setIsConnected(false);
    setSession(null);
    setAccount(null);
    setIsLoading(false);

    // Redirect to home instead of full reload
    window.location.href = "/";
  }, [providerClient]);

  const validateSession = useCallback(
    async (session: SessionTypes.Struct) => {
      try {
        // Check if session is expired
        const now = Date.now();
        if (session.expiry * 1000 < now) {
          await disconnect();
          return false;
        }
        return true;
      } catch (error) {
        logger.error(
          "walletconnect",
          ErrorCodes.WC_002,
          "WalletConnect session validation failed",
          error as Error
        );
        await disconnect();
        return false;
      }
    },
    [disconnect]
  );

  // Event handlers extracted with useCallback to enable proper cleanup
  const handleSessionUpdate = useCallback((session: SessionTypes.Struct) => {
    setSession(session);
  }, []);

  const handleDisplayUri = useCallback(
    (uri: string) => {
      setIsLoading(false);
      const modal = initializeModal(providerClient);
      modal
        ?.open({ uri, view: "Connect", namespace: "cosmos" })
        .then(() => {
          console.log("modal opened");
        })
        .catch((error) => {
          logger.error(
            "walletconnect",
            ErrorCodes.WC_001,
            "WalletConnect modal failed to open",
            error as Error
          );
        });
    },
    [providerClient]
  );

  const handleConnect = useCallback(
    async (session: SessionTypes.Struct) => {
      setSession(session);
      setIsConnected(() => !!session);
      const modal = initializeModal(providerClient);
      await modal?.close();
    },
    [providerClient]
  );

  const handleDisconnectEvent = useCallback(() => {
    console.log("disconnect emitted");
    handleDisconnect();
  }, [handleDisconnect]);

  const handleSessionDeleteEvent = useCallback(() => {
    console.log("session_delete");
    handleDisconnect();
  }, [handleDisconnect]);

  // Cleanup connection timeout on unmount
  useEffect(() => {
    return () => {
      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (providerClient) {
      setIsLoading(false);
    }

    // Register event listeners with named references
    providerClient?.on("session_update", handleSessionUpdate);
    providerClient?.on("disconnect", handleDisconnectEvent);
    providerClient?.on("display_uri", handleDisplayUri);
    providerClient?.on("connect", handleConnect);
    providerClient?.on("session_delete", handleSessionDeleteEvent);

    // Cleanup with the same named references
    return () => {
      providerClient?.off("session_update", handleSessionUpdate);
      providerClient?.off("disconnect", handleDisconnectEvent);
      providerClient?.off("display_uri", handleDisplayUri);
      providerClient?.off("connect", handleConnect);
      providerClient?.off("session_delete", handleSessionDeleteEvent);
    };
  }, [
    providerClient,
    handleSessionUpdate,
    handleDisconnectEvent,
    handleDisplayUri,
    handleConnect,
    handleSessionDeleteEvent,
  ]);

  useEffect(() => {
    setAccount(extractAccountAddressFromSession(session));
  }, [session]);

  useEffect(() => {
    const init = async () => {
      try {
        setIsLoading(true);
        const provider = await initializeProvider();
        setProviderClient(provider);
        initializeModal(provider);
        // Check for existing sessions (your current approach)
        const existingSessions = provider.client.session.getAll();
        const lastKeyIndex = existingSessions.length - 1;
        const lastSession = existingSessions[lastKeyIndex];

        if (lastSession) {
          // Session exists, restore connection
          setSession(lastSession);
          setAccount(extractAccountAddressFromSession(lastSession));
          setIsConnected(true);

          // Validate session is still active
          await validateSession(lastSession);
        }
      } catch (error) {
        logger.error(
          "walletconnect",
          ErrorCodes.WC_001,
          "WalletConnect provider initialization failed",
          error as Error
        );
        setIsLoading(false);
      }
    };
    if (!providerClient) {
      init();
    }
  }, [providerClient, validateSession]);

  // Wallet request cannot be cancelled on the wallet side; we only stop waiting locally.
  const SEND_REQUEST_TIMEOUT_MS = 30000;

  const sendRequest = useCallback(
    async (messages: TMessageCreateData[]): Promise<SendRequestResult> => {
      if (!providerClient) {
        return { ok: false, error: new Error("Provider not initialized") };
      }

      const timeoutPromise = new Promise<{ timeout: true }>((resolve) => {
        setTimeout(() => resolve({ timeout: true }), SEND_REQUEST_TIMEOUT_MS);
      });

      const requestPromise = providerClient
        .request(
          {
            method: "self_sendDirect",
            params: messages,
          },
          CHAIN_ID
        )
        .then((res) => ({
          ok: true as const,
          data: typeof res === "string" ? res : String(res),
        }))
        .catch((error) => ({
          ok: false as const,
          error: error instanceof Error ? error : new Error(String(error)),
        }));

      const raceResult = await Promise.race([requestPromise, timeoutPromise]);

      if ("timeout" in raceResult && raceResult.timeout) {
        logger.error(
          "walletconnect",
          ErrorCodes.WC_003,
          "WalletConnect request timeout",
          undefined,
          {
            timeoutMs: SEND_REQUEST_TIMEOUT_MS,
          }
        );
        await logger.flush();
        handleDisconnect();
        return { ok: false, error: new SendRequestTimeoutError() };
      }
      return raceResult as SendRequestResult;
    },
    [providerClient, handleDisconnect]
  );

  // Memoize the context value to prevent unnecessary re-renders
  const valueProvider: connectionContextType = useMemo(
    () => ({
      isConnected,
      address: account || undefined,
      session,
      isLoading,
      account: account || undefined,
      providerClient: providerClient || undefined,
      connect,
      disconnect: handleDisconnect,
      sendRequest,
    }),
    [
      isConnected,
      account,
      session,
      isLoading,
      providerClient,
      handleDisconnect,
      sendRequest,
      connect,
    ]
  );

  return <ConnectionContext.Provider value={valueProvider}>{children}</ConnectionContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(ConnectionContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within a ConnectionProvider");
  }
  return context;
};
